import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Truck, AlertTriangle } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useCamions, useCreateCamion, useUpdateCamion, useEquipes,
} from '@/hooks/useTransport';
import {
  STATUT_CAMION_LABELS,
  type StatutCamion,
  type Camion,
} from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUT_BADGE: Record<StatutCamion, string> = {
  disponible:   'ds-bd ds-bd-s',
  en_rotation:  'ds-bd ds-bd-i',
  maintenance:  'ds-bd ds-bd-w',
  hors_service: 'ds-bd ds-bd-d',
};

export const FlotteCamionsPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutCamion | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<Camion | undefined>();

  const { data, isLoading, refetch } = useCamions({ statut: statut || undefined, actif: true, limit: 200 });
  const camions = data?.data ?? [];

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (c: Camion) => { setEditing(c); setDialogOpen(true); };

  const count = (s: StatutCamion) => camions.filter((c) => c.statut === s).length;

  return (
    <>
      <Topbar
        title="Flotte camions"
        subtitle={filiale ? `Transport minier · ${filiale.fullName}` : 'Flotte camions'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}>
              <Plus size={14} /> Nouveau camion
            </button>
          </>
        }
      />

      <div className="ds-content">
        {/* KPI strip */}
        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1">
            <div className="ds-kr-ico">🚛</div>
            <div className="ds-kr-val">{camions.length}</div>
            <div className="ds-kr-lbl">Flotte totale</div>
          </div>
          <div className="ds-kr kr-c3">
            <div className="ds-kr-ico">✅</div>
            <div className="ds-kr-val">{count('disponible')}</div>
            <div className="ds-kr-lbl">Disponibles</div>
          </div>
          <div className="ds-kr kr-c2">
            <div className="ds-kr-ico">🏃</div>
            <div className="ds-kr-val">{count('en_rotation')}</div>
            <div className="ds-kr-lbl">En rotation</div>
          </div>
          <div className="ds-kr kr-c4">
            <div className="ds-kr-ico">🔧</div>
            <div className="ds-kr-val">{count('maintenance')}</div>
            <div className="ds-kr-lbl">Maintenance</div>
          </div>
          <div className="ds-kr kr-c5">
            <div className="ds-kr-ico">⛔</div>
            <div className="ds-kr-val">{count('hors_service')}</div>
            <div className="ds-kr-lbl">Hors service</div>
          </div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutCamion | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_CAMION_LABELS) as StatutCamion[]).map((s) => (
              <option key={s} value={s}>{STATUT_CAMION_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Camions</div>
              <div className="ds-table-sub">{camions.length} camion{camions.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Code</th>
                <th style={{ width: 140 }}>Immat.</th>
                <th>Marque / Modèle</th>
                <th className="ds-am" style={{ width: 100 }}>Capacité</th>
                <th>Chauffeur</th>
                <th>Équipe</th>
                <th style={{ width: 130 }}>Statut</th>
                <th style={{ width: 130 }}>Prochain entretien</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              )}
              {!isLoading && camions.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                    <Truck size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun camion enregistré</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}>
                      <Plus size={12} /> Créer le premier camion
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && camions.map((c) => {
                const entretienAlert = c.prochainEntretien
                  ? new Date(c.prochainEntretien).getTime() - Date.now() < 15 * 24 * 3600 * 1000
                  : false;
                return (
                  <tr key={c.id}>
                    <td><span className="ds-ref-code">{c.code}</span></td>
                    <td style={{ fontSize: 12, fontFamily: 'var(--font-m)' }}>{c.immatriculation ?? '—'}</td>
                    <td style={{ fontSize: 12 }}>{c.marque ?? '—'}{c.modele ? ` · ${c.modele}` : ''}</td>
                    <td className="ds-am">{formatNumber(parseFloat(c.capaciteTonnes))} t</td>
                    <td style={{ fontSize: 12 }}>{c.chauffeurNom ?? '—'}</td>
                    <td style={{ fontSize: 12 }}>{c.equipe ? `${c.equipe.code} — ${c.equipe.nom}` : '—'}</td>
                    <td><span className={STATUT_BADGE[c.statut]}>{STATUT_CAMION_LABELS[c.statut]}</span></td>
                    <td style={{ fontSize: 12, color: entretienAlert ? 'var(--warning)' : 'var(--tx-3)', fontWeight: entretienAlert ? 600 : 400 }}>
                      {entretienAlert && '⚠ '}{fmtDate(c.prochainEntretien)}
                    </td>
                    <td>
                      <button onClick={() => openEdit(c)} className="ds-row-btn" title="Modifier">
                        <Edit size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CamionFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} camion={editing} />
    </>
  );
};

// ── Dialog création/édition camion ──────────────────────────

const CamionFormDialog = ({ open, onClose, camion }: { open: boolean; onClose: () => void; camion?: Camion }) => {
  const isEdit = !!camion;
  const [code, setCode]                           = useState('');
  const [immatriculation, setImmatriculation]     = useState('');
  const [marque, setMarque]                       = useState('');
  const [modele, setModele]                       = useState('');
  const [capaciteTonnes, setCapaciteTonnes]       = useState('');
  const [chauffeurNom, setChauffeurNom]           = useState('');
  const [equipeId, setEquipeId]                   = useState('');
  const [statut, setStatut]                       = useState<StatutCamion>('disponible');
  const [kmActuel, setKmActuel]                   = useState('');
  const [dateMiseEnService, setDateMiseEnService] = useState('');
  const [prochainEntretien, setProchainEntretien] = useState('');
  const [notes, setNotes]                         = useState('');
  const [error, setError]                         = useState<string | null>(null);

  const { data: equipesData } = useEquipes({ statut: 'actif', limit: 200 });
  const equipes = equipesData?.data ?? [];
  const create  = useCreateCamion();
  const update  = useUpdateCamion();

  useEffect(() => {
    if (!open) return;
    if (camion) {
      setCode(camion.code);
      setImmatriculation(camion.immatriculation ?? '');
      setMarque(camion.marque ?? '');
      setModele(camion.modele ?? '');
      setCapaciteTonnes(camion.capaciteTonnes);
      setChauffeurNom(camion.chauffeurNom ?? '');
      setEquipeId(camion.equipeId ?? '');
      setStatut(camion.statut);
      setKmActuel(camion.kmActuel?.toString() ?? '');
      setDateMiseEnService(camion.dateMiseEnService?.slice(0, 10) ?? '');
      setProchainEntretien(camion.prochainEntretien?.slice(0, 10) ?? '');
      setNotes(camion.notes ?? '');
    } else {
      setCode(''); setImmatriculation(''); setMarque(''); setModele('');
      setCapaciteTonnes(''); setChauffeurNom(''); setEquipeId(''); setStatut('disponible');
      setKmActuel(''); setDateMiseEnService(''); setProchainEntretien(''); setNotes('');
    }
    setError(null);
  }, [open, camion]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        code: code.trim(),
        immatriculation:   immatriculation.trim() || undefined,
        marque:            marque.trim() || undefined,
        modele:            modele.trim() || undefined,
        capaciteTonnes:    parseFloat(capaciteTonnes || '0'),
        chauffeurNom:      chauffeurNom.trim() || undefined,
        equipeId:          equipeId || undefined,
        statut,
        kmActuel:          kmActuel ? parseInt(kmActuel, 10) : undefined,
        dateMiseEnService: dateMiseEnService || undefined,
        prochainEntretien: prochainEntretien || undefined,
        notes:             notes.trim() || undefined,
      };
      if (isEdit && camion) {
        await update.mutateAsync({ id: camion.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = code.trim() && parseFloat(capaciteTonnes || '0') > 0;

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${camion?.code}` : 'Nouveau camion'} subtitle="Transport minier" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Code *">
            <input value={code} onChange={(e) => setCode(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="C-001" />
          </Field>
          <Field label="Immatriculation">
            <input value={immatriculation} onChange={(e) => setImmatriculation(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="AB-1234-CI" />
          </Field>
          <Field label="Capacité (t) *">
            <input type="number" min="0.1" step="0.1" value={capaciteTonnes} onChange={(e) => setCapaciteTonnes(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="40" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Marque">
            <input value={marque} onChange={(e) => setMarque(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Mercedes" />
          </Field>
          <Field label="Modèle">
            <input value={modele} onChange={(e) => setModele(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Actros 4148" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Chauffeur">
            <input value={chauffeurNom} onChange={(e) => setChauffeurNom(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Koné Mamadou" />
          </Field>
          <Field label="Équipe affectée">
            <select value={equipeId} onChange={(e) => setEquipeId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
              <option value="">— Aucune —</option>
              {equipes.map((e) => (
                <option key={e.id} value={e.id}>{e.code} — {e.nom}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Statut">
            <select value={statut} onChange={(e) => setStatut(e.target.value as StatutCamion)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(STATUT_CAMION_LABELS) as StatutCamion[]).map((s) => (
                <option key={s} value={s}>{STATUT_CAMION_LABELS[s]}</option>
              ))}
            </select>
          </Field>
          <Field label="Km actuel">
            <input type="number" min="0" step="1" value={kmActuel} onChange={(e) => setKmActuel(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="125000" />
          </Field>
          <Field label="Mise en service">
            <input type="date" value={dateMiseEnService} onChange={(e) => setDateMiseEnService(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
        </div>

        <Field label="Prochain entretien">
          <input type="date" value={prochainEntretien} onChange={(e) => setProchainEntretien(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} placeholder="Dernière révision, particularités…" />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>
            {isEdit ? 'Enregistrer' : 'Créer le camion'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
